import { useState, useEffect, useRef } from 'react';
import { timeTrackingAPI } from '../utils/api';
import { useToast, apiError } from '../components/Toast';
import { Play, Square } from 'lucide-react';


const fmt = (sec) => {
  const h = Math.floor(sec/3600), m = Math.floor((sec%3600)/60), s = sec%60;
  return [h,m,s].map(v=>String(v).padStart(2,'0')).join(':');
};

/**
 * Start/stop timer widget for an activity. Usage:
 *   <TimeTracker activityId={activity.id} isRunning={!!activity.active_timer_started_at}
 *                startedAt={activity.active_timer_started_at} onChange={reload} />
 */
export default function TimeTracker({ activityId, isRunning, startedAt, onChange }) {
  const toast = useToast();
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning && startedAt) {
      const tick = () => setElapsed(Math.floor((Date.now() - new Date(startedAt).getTime())/1000));
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      setElapsed(0);
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, startedAt]);

  const handleStart = async () => {
    setBusy(true);
    try { await timeTrackingAPI.start(activityId); onChange?.(); }
    catch(e) { toast.error(apiError(e, 'Could not start timer')); }
    finally { setBusy(false); }
  };
  const handleStop = async () => {
    setBusy(true);
    try { await timeTrackingAPI.stop(activityId); onChange?.(); }
    catch(e) { toast.error(apiError(e, 'Could not stop timer')); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2">
      {isRunning && <span className="text-xs font-mono font-bold text-emerald-500">{fmt(elapsed)}</span>}
      <button onClick={isRunning ? handleStop : handleStart} disabled={busy}
        className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg font-medium transition-colors ${
          isRunning ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400'
                    : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400'
        }`}>
        {isRunning ? <Square size={13}/> : <Play size={13}/>} {isRunning ? 'Stop' : 'Start'}
      </button>
    </div>
  );
}
